import { useSuspenseQuery } from '@tanstack/react-query';
import { ISbStoryData } from 'storyblok-js-client';

import { getSlugWithDash } from '@natu/utils/getSlugWithDash';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { getStoryblokSdk } from '../../../../api/api';
import { useDraftModeContext } from '../../../../storyblok-preview';

const fieldsToExclude = [
  'body',
  'afterBody',
  'customPreHeader',
  'customHeader',
  'seoUnitText',
  'seoBaseSalary',
  'seoDescription',
  'seoTelecommute',
  'seoEmploymentType',
  'disablePreHeader',
  'link',
  'locations',
];

type GetJobOfferItemsInput = {
  jobOfferItems: string[];
  draftMode: boolean;
};

export type JobOfferItem = {
  uuid: string;
  fullSlug: string;
  title: string;
  description: string;
  tag?: { name: string; color: string };
};

export const getJobOfferItemsQueryKey = ({ jobOfferItems, draftMode }: GetJobOfferItemsInput) => [
  'jobOfferItems',
  jobOfferItems,
  { draftMode },
];

export const getJobOfferItems = async ({
  jobOfferItems,
  draftMode,
}: GetJobOfferItemsInput): Promise<JobOfferItem[]> => {
  const { getContentNodes } = getStoryblokSdk({ draftMode });

  const { data } = await getContentNodes({
    filterQuery: { component: { in: 'careerOffer' } },
    byUuids: jobOfferItems.join(','),
    byUuidsOrdered: jobOfferItems.join(','),
    excludingFields: fieldsToExclude.join(','),
    relations: 'careerOffer.careerOfferTag',
  });

  return data?.stories.map((story: ISbStoryData) => ({
    uuid: story?.uuid,
    title: story?.content?.title,
    description: story?.content?.salaryRange,
    fullSlug: getSlugWithDash(story?.full_slug),
    tag: {
      name: story?.content?.careerOfferTag?.name,
      color: story?.content?.careerOfferTag?.content?.color?.value,
    },
  }));
};

type UseJobOfferItemsInput = {
  jobOfferItems: GetJobOfferItemsInput['jobOfferItems'];
};

export const useJobOfferItems = ({ jobOfferItems }: UseJobOfferItemsInput) => {
  const { draftMode } = useDraftModeContext();

  const { data } = useSuspenseQuery({
    queryKey: getJobOfferItemsQueryKey({ jobOfferItems, draftMode }),
    queryFn: async () => getJobOfferItems({ jobOfferItems, draftMode }),
  });

  return {
    data,
    isEmpty: !isArrayWithLength(data),
  };
};
